const DOG_ADULT_RANGES = {
  obese: [5, 6],
  slightly_obese: [6, 7],
  standard: [8, 9],
  slightly_thin: [9, 11],
  thin: [10, 12],
};

const DOG_PUPPY_MONTHS = {
  small: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  medium: [2, 3, 5, 7, 9, 11, 13, 14, 15, 16, 17, 18],
  large: [2, 4, 6, 9, 12, 14, 17, 18, 19, 20, 22, 23, 24],
};

const DOG_PUPPY_FACTORS = {
  small: [30, 27.5, 25, 22.5, 20, 17.5, 15, 13.75, 12.5, 11.25, 10],
  medium: [30, 27.5, 25, 22.5, 20, 17.5, 15, 13.75, 12.5, 11.25, 10, 8.75],
  large: [30, 27.5, 25, 22.5, 20, 17.5, 15, 13.75, 12.5, 11.25, 10, 8.75, 8],
};

const DOG_PUPPY_BODY_FACTORS = {
  obese: 0.7,
  slightly_obese: 0.85,
  standard: 1,
  slightly_thin: 1.25,
  thin: 1.4,
};

const CAT_KITTEN_FACTORS = [30, 27.5, 25, 22.5, 20, 17.5, 15, 13.75, 12.5, 11.25, 10];

class K9FeedingCalculator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.tabs = [...this.querySelectorAll('[data-k9-feeding-tab]')];
    this.panels = [...this.querySelectorAll('[data-k9-feeding-panel]')];
    this.forms = [...this.querySelectorAll('[data-k9-feeding-form]')];

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.k9FeedingTab));
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event));
    });

    this.forms.forEach((form) => {
      form.addEventListener('submit', (event) => this.handleSubmit(event));
      form.addEventListener('reset', () => window.requestAnimationFrame(() => this.resetForm(form)));
    });

    this.querySelector('[data-k9-dog-age]')?.addEventListener('change', () => this.syncConditionalFields('dog'));
    this.querySelector('[data-k9-cat-age]')?.addEventListener('change', () => this.syncConditionalFields('cat'));
  }

  selectTab(type, moveFocus = false) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.k9FeedingTab === type;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && moveFocus) tab.focus();
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.k9FeedingPanel !== type;
    });
  }

  handleTabKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;

    this.selectTab(this.tabs[nextIndex].dataset.k9FeedingTab, true);
  }

  syncConditionalFields(type) {
    const isDog = type === 'dog';
    const age = this.querySelector(isDog ? '[data-k9-dog-age]' : '[data-k9-cat-age]');
    const field = this.querySelector(isDog ? '[data-k9-dog-month-field]' : '[data-k9-cat-month-field]');
    const input = this.querySelector(isDog ? '[data-k9-dog-month]' : '[data-k9-cat-month]');
    const needsMonth = age?.value === (isDog ? 'puppy' : 'under1');

    field.hidden = !needsMonth;
    input.required = needsMonth;
    if (!needsMonth) input.value = '';
    this.hideFeedback(age.closest('form'));
  }

  handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    this.hideFeedback(form);

    if (!form.reportValidity()) return;

    const type = form.dataset.k9FeedingForm;
    const calculation = type === 'dog' ? this.calculateDog(form) : this.calculateCat(form);
    if (calculation.error) {
      this.showError(form, calculation.error);
      return;
    }

    this.showResult(form, calculation.amount, type === 'dog' ? 1 : 0);
  }

  calculateDog(form) {
    const data = new FormData(form);
    const age = data.get('age');
    const size = data.get('size');
    const bodyType = data.get('body-type');
    const weight = Number.parseFloat(data.get('weight'));

    if (!Number.isFinite(weight) || weight <= 0) {
      return { error: '体重は0より大きい数値で入力してください。' };
    }

    if (age === 'adult') {
      return { amount: this.calculateAdultDog(weight, bodyType) };
    }

    const month = Number.parseInt(data.get('month'), 10);
    if (!Number.isInteger(month) || month < 2) {
      return { error: '幼犬の月齢は2か月以上の整数で入力してください。' };
    }

    // W2の現行仕様を維持し、小・中型犬は12か月超、大型犬は24か月超を成犬計算へ切り替える。
    const maxPuppyMonth = size === 'large' ? 24 : 12;
    if (month > maxPuppyMonth) {
      return { amount: this.calculateAdultDog(weight, bodyType) };
    }

    const months = DOG_PUPPY_MONTHS[size];
    const factors = DOG_PUPPY_FACTORS[size];
    let factorIndex = months.findIndex((allowedMonth) => allowedMonth === month);

    if (factorIndex === -1) {
      for (let index = months.length - 1; index >= 0; index -= 1) {
        if (month >= months[index]) {
          factorIndex = index;
          break;
        }
      }
    }

    if (factorIndex === -1) factorIndex = 0;
    const amount = weight * factors[factorIndex] * DOG_PUPPY_BODY_FACTORS[bodyType];
    return { amount: this.truncateToOneDecimal(amount) };
  }

  calculateAdultDog(weight, bodyType) {
    const [minimum, maximum] = DOG_ADULT_RANGES[bodyType];
    return this.truncateToOneDecimal(weight * ((minimum + maximum) / 2));
  }

  calculateCat(form) {
    const data = new FormData(form);
    const age = data.get('age');
    const weight = Number.parseFloat(data.get('weight'));

    if (!Number.isFinite(weight) || weight <= 0) {
      return { error: '体重は0より大きい数値で入力してください。' };
    }

    if (age === 'under1') {
      const month = Number.parseInt(data.get('month'), 10);
      if (!Number.isInteger(month) || month < 2 || month > 12) {
        return { error: '1歳未満の月齢は2〜12か月の整数で入力してください。' };
      }

      return { amount: Math.round(weight * CAT_KITTEN_FACTORS[month - 2]) };
    }

    if (weight < 1 || weight > 10) {
      return { error: '1歳以上の愛猫の体重は1〜10kgの範囲で入力してください。' };
    }

    return { amount: Math.floor(weight) * 8 };
  }

  truncateToOneDecimal(value) {
    return Math.floor(value * 10) / 10;
  }

  showResult(form, amount, fractionDigits) {
    const result = form.querySelector('[data-k9-feeding-result]');
    const number = result.querySelector('[data-k9-feeding-result-number]');
    number.textContent = amount.toFixed(fractionDigits);
    result.hidden = false;
    result.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });
  }

  showError(form, message) {
    const error = form.querySelector('[data-k9-feeding-error]');
    error.textContent = message;
    error.hidden = false;
    error.focus?.();
  }

  hideFeedback(form) {
    const error = form.querySelector('[data-k9-feeding-error]');
    const result = form.querySelector('[data-k9-feeding-result]');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
    if (result) {
      result.hidden = true;
      const number = result.querySelector('[data-k9-feeding-result-number]');
      if (number) number.textContent = '--';
    }
  }

  resetForm(form) {
    this.hideFeedback(form);
    this.syncConditionalFields(form.dataset.k9FeedingForm);
    form.querySelector('select, input')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('k9-feeding-calculator')) {
  customElements.define('k9-feeding-calculator', K9FeedingCalculator);
}
